import { z } from 'zod';

import { DilutionCondition } from '../../../types/pool-plans.types';
import { getTranslation } from '../../../utils/get-translation.util';

export const MAX_FILE_SIZE = 1024 * 1024 * 10;

export const ACCEPTED_FILE_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/csv',
  'image/png',
  'image/jpeg',
];
export const fileSchemaMessage = getTranslation('FILE_SIZE_TOO_LARGE');

// Editing an existing pool never changes its size here: the field is read-only and the Adjust Pool
// Size flow owns the granted-shares floor (shared `resolvePoolSizeAdjustment`).
export const stepOne = z.object({
  name: z
    .string()
    .trim()
    .min(1, { message: getTranslation('REQUIRED') })
    .max(100, getTranslation('MAXIMUM_CHARACTERS', { count: 100 })),
  dillutedSharesCount: z.coerce
    .number({ message: getTranslation('PLEASE_ENTER_NUMERIC_VALUE') })
    .min(1, { message: getTranslation('REQUIRED') }),
  shareClass: z.object({
    name: z.string(),
    id: z.string(),
  }),
  date: z.coerce.date(),
  dilutionCondition: z.enum(DilutionCondition),
});

export const stepTwo = z
  .object({
    additionalNotes: z
      .string()
      .max(3000, getTranslation('MAXIMUM_CHARACTERS', { count: 3000 }))
      .optional(),
    files: z
      .object({
        docLink: z.string(),
        loadProgress: z.number().max(100),
        abort: z.function(),
        id: z.string(),
        doc: z
          .custom<File>()
          .or(
            z.object({
              size: z.number(),
              type: z.string(),
              name: z.string(),
            }),
          )
          .refine(
            ({ type }) => ACCEPTED_FILE_MIME_TYPES.includes(type),
            getTranslation('FILE_FORMAT_NOT_SUPPORTED'),
          )
          .refine(({ size }) => size <= MAX_FILE_SIZE, fileSchemaMessage),
      })
      .array()

      .optional(),
  })
  .optional();

export const formSchema = z.object({
  stepOne,
  stepTwo,
});

export type FormSchema = z.infer<typeof formSchema>;
